import WRAP from "WRAP-JS";
import {
  configVolcanicAshDh,
  configVolcanicAshLayer,
  configVolcanicRankDh,
  configVolcanicRankLayer
} from "./config/ConfigAcos";
import LegendAcos from "../legend/LegendAcos";
import { formatUTCDate, formatValidTime, parseUTCDate, _orderBy, m2ft, ft2m } from "../../../utils/FPEUtils";
import WrapOption from "../../../constants/WrapOption";
import axios from "axios";
import reactDom from "react-dom";
import Draggable from "react-draggable";
import { WX_ID_ACOS } from "../../../store/WeatherContentsState";

class Acos {
  static NAME = WX_ID_ACOS;
  static AshLayerName = 'VolcanicAsh_GPV';
  static RankLayerName = 'volcanicRank';
  static LabelLayer = "AcosLabelLayer";

  dispatcher;
  mapColor;
  ashLayer;
  rankLayer;
  labelLayer;
  ready = false;
  dhAsh;
  dhRank;
  visible;
  altitude = 0;
  elapsed = 0;
  etd;
  latestVolcanoList;
  latestAnnouncedDate;
  validTimeList;
  validTime;
  level;
  popupVolcanoList;
  popup;
  rankIndex;
  volcanoAsh2D;
  volcanoAsh3D;

  constructor(mapColor) {
    this.mapColor = mapColor;

    this.ashLayer = new WRAP.Geo.Layer(Acos.AshLayerName);
    this.rankLayer = new WRAP.Geo.Layer(Acos.RankLayerName);
    this.labelLayer = new WRAP.Geo.Layer(Acos.LabelLayer);
    this.ashLayer.setVisible(false);
    this.ashLayer.setTooltip(this._ashTooltip.bind(this));
    this.rankLayer.setVisible(false);
    this.rankLayer.setTooltip(this._rankTooltip)
    this.labelLayer.setVisible(false);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    return new Promise(allResolve => {

      const dhAsh = this.dhAsh = new WRAP.Data(configVolcanicAshDh);
      const dhRank = this.dhRank = new WRAP.Data(configVolcanicRankDh);

      const ashPromise = new Promise(resolve => {
        dhAsh.updated('index', timelist => {
          this._selectBaseValidTime(timelist);
          resolve(timelist);
        });
      });

      const rankPromise = new Promise(resolve => {
        dhRank.updated('data', data => {
          // アイコンの描画順制御の為ソートする
          data.features.sort((a,b)=> {
            if(a.properties.index < b.properties.index) {
              return -1;
            } else if (a.properties.index > b.properties.index) {
              return 1;
            } else {
              return 0;
            }
          });
          resolve(data);
        });
      });

      const ashVectorPromise = this._loadVolcanicAshConcVector();

      Promise.all([ashPromise, rankPromise, ashVectorPromise]).then(refs => {
        this._createPopupVolcanoList();
        allResolve(refs);
      });
    });
  }

  initLayer() {
    this.ashLayer.configure(this.dhAsh, configVolcanicAshLayer);
    this.rankLayer.configure(this.dhRank, configVolcanicRankLayer);
    this.rankLayer.updated(this._addLabel.bind(this));
    WRAP.Geo.addEventHandler('touch', this._acosTouchHandler.bind(this));
    WRAP.Geo.addEventHandler('boundsChange', this._updateBounds.bind(this));
  }

  async drawLayer(visible) {
    this.visible = visible;
    if(!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this._allLayers();
  }
  _allLayers() {
    return [
      this.ashLayer,
      this.rankLayer,
      this.labelLayer
    ]
  }

  setVisible(visible) {
    if(!this.ready) return;

    if(this.ashLayer.visible() === visible) {
      return;
    }

    if(visible) {
      this.ashLayer.show();
      this.rankLayer.show();
      this.labelLayer.show();
    } else {
      this.ashLayer.hide();
      this.rankLayer.hide();
      this.labelLayer.hide();
    }
    this.dhAsh.setAutoUpdate('datalist', visible);
    this.dhRank.setAutoUpdate('data', visible);

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  getTimeInformation() {
    return {
      id: Acos.NAME,
      label: 'ACOS',
      validTime: formatValidTime(this.validTime)
    };
  }

  getLegend() {
    return {
      id: Acos.NAME,
      label: 'ACOS',
      component: LegendAcos,
      contents: this
    }
  }

  getIndividualAltitude() {
    return {
      id: Acos.NAME,
      altitude: this.altitude,
      label: 'ACOS',
      tooltip: this._altitudeTooltip.bind(this)
    }
  }

  getAltitudeLabel() {
    return this._altitudeTooltip(this.altitude)
  }

  applyIndividualAltitude(altitude) {
    if(this.altitude !== altitude) {
      this.altitude = altitude;
      if(!this.ready) return;
      const level = this._convertAltitudeToNearFL(this.altitude)
      if(this.level !== level) {
        this._updateDataset();
      }
    }
  }

  applyElapsed(elapsed) {
    this.elapsed = elapsed;

    if(!this.ready) return;

    this._selectBaseValidTime(this.dhAsh.retained.index);
  }

  applyPlanETD(etd) {
    this.etd = etd;

    if(!this.ready) return;

    this._selectBaseValidTime(this.dhAsh.retained.index);
  }

  changeRoutePointEditing(routePointEditing) {
    this.ashLayer.disableInteraction(routePointEditing);
    this.rankLayer.disableInteraction(routePointEditing);
  }

  _selectBaseValidTime(timeList) {
    
    if(!timeList) return;

    this.latestVolcanoList = [];
    let latestIssueDateTime = 0;
    let latestAnnouncedDate;
    // 各火山の最新のデータかつ、no_detection_flagが0のものに絞る
    Object.keys(timeList).forEach(key => {
      let volcanoData = timeList[key];
      const latestEruptedData = _orderBy(volcanoData, 'erupted_date', 'desc')[0];
      if(latestEruptedData.no_detection_flag !== 1) {

        this.latestVolcanoList.push({
          id: key,
          latestErupted: latestEruptedData.erupted_date,
          eruptedFlag: latestEruptedData.erupted_flag,
          floatingAshFlag: latestEruptedData.floating_ash_flag
        });
        volcanoData = latestEruptedData;

        // 火山中最新のissueDateを探す
        const issueDate = new WRAP.Core.DateTime(latestEruptedData.issued_date);
        if(!latestIssueDateTime || latestIssueDateTime.diff(issueDate) > 0) {
          latestIssueDateTime = issueDate;
        }

        // 火山中最新のannounced_dateを探す
        const announcedDate = new WRAP.Core.DateTime(latestEruptedData.announced_date);
        if(!latestAnnouncedDate || latestAnnouncedDate.diff(announcedDate) > 0) {
          latestAnnouncedDate = announcedDate;
        }

      } else {
        delete timeList[key];
      }
    });

    // 最新のAnnounceDateから24h分のvalidtimeをつくる
    if(latestAnnouncedDate.date.getUTCMinutes() < 30) {
      latestAnnouncedDate.date.setUTCMinutes(0,0,0);
    } else {
      latestAnnouncedDate.date.setUTCHours(latestAnnouncedDate.date.getUTCHours() + 1, 0, 0, 0);
    }
    this.validTimeList = [];
    for(let i = 0; i <= 24; i++) {
      this.validTimeList.push(latestAnnouncedDate.text());
      latestAnnouncedDate.date.setHours(latestAnnouncedDate.date.getHours() + 1);
    }

    const tmpTimeList = this.validTimeList.map(validTime => {
      return {
        "validtime": validTime,
        "validtime_msec": parseUTCDate(validTime).getTime()
      };
    });

    let targetTime;
    if(!this.etd) {
      targetTime = Date.now();
    } else {
      targetTime = this.etd.getTime() + this.elapsed * 1000;
    }

    const sortedTimeList = _orderBy(tmpTimeList, [
      o => {
        return Math.abs(targetTime - o.validtime_msec);
      },
      'validtime'
    ], ['asc', 'desc']);

    const selectedTime = sortedTimeList[0];

    if(selectedTime && this.validTime !== selectedTime.validtime) {
      this.validTime = selectedTime.validtime;
      // Layerのデータ更新
      this._updateDataset();
    }
  }

  _updateDataset() {

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});

    this.level = this._convertAltitudeToNearFL(this.altitude);
    const volcanoList = [];
    const validDateTime = new WRAP.Core.DateTime(this.validTime);
    Object.keys(this.latestVolcanoList).forEach(key => {
      const volcano = this.latestVolcanoList[key];
      volcanoList.push({
        id: volcano.id,
        level: this.level,
        basetime: volcano.latestErupted,
        ft: this._createFt(new WRAP.Core.DateTime(volcano.latestErupted), validDateTime)
      })
    });
    this.ashLayer.set(volcanoList);
  }

  _createFt(eruptedDateTime, validDateTime) {
    const diffTime = eruptedDateTime.diff(validDateTime);
    let ft = 0;
    if(diffTime < 0) {
      ft = 0;
    } else if(diffTime <= 3600 * 4.5) {
      ft = 3;
    } else if(diffTime <= 3600 * 7.5) {
      ft = 6;
    } else if(diffTime <= 3600 * 10.5) {
      ft = 9;
    } else if(diffTime <= 3600 * 13.5) {
      ft = 12;
    } else if(diffTime <= 3600 * 16.5) {
      ft = 15;
    } else if(diffTime <= 3600 * 19.5) {
      ft = 18;
    } else if(diffTime <= 3600 * 22.5) {
      ft = 21;
    } else if(diffTime <= 3600 * 27) {
      ft = 24;
    }
    return [ft];
  }

  _rankTooltip(f) {
    if (!f) {
      return null;
    }
    const prop = f.properties;
    const geo = f.geometry;
    let ft = ' --ft';
    if (geo) {
      const v = geo.coordinates[2];
      ft = ` ${Math.floor(v * 3.28084)}ft`;
    }
    if (prop) {
      return prop.name + ft;
    }
    return null;
  };

  _ashTooltip(param) {
    const prop = param.properties;

    let displayFl;
    if(prop.fl === "ALL") {
      const volcanoAsh = this.volcanoAsh3D[prop.id];
      let ashData;
      if(volcanoAsh) {
        ashData = volcanoAsh.data;
      }
      if(ashData && ashData[prop.basetime]) {

        const coordinates = ashData[prop.basetime].ft.find(f=>f.fth === prop.validtime).coordinates;
        const minFlFt = this._meter2ft(coordinates[0][0][0][2]);
        const maxFlFt = this._meter2ft(coordinates[coordinates.length - 1][0][0][2]);
        displayFl = `${minFlFt}-${maxFlFt}`;
      }
    } else {
      displayFl = prop.fl.replace("FL", "");
    }

    const validtime = (new WRAP.Core.DateTime(prop.validtime)).text('MM/DD hh:mmZ');
    return `<div style="text-align: left;">FT=${prop.ft} FL=${displayFl} <br> ${validtime}<div>`;
  }

  _addLabel() {
    const fontSize = 12;
    const offsetX = 11;
    const offsetY = 18;
    const fillStyle = this.mapColor === "white" ? "#000" : "#FFF";
    this.labelLayer.clear();
    const mask = WRAP.Geo.getMask();
    mask.clear();
    
    const features = this.dhRank.retained.data.features.concat();
    features.sort((a, b)=> {
      if(a.properties.index < b.properties.index) {
        return 1;
      } else if (a.properties.index > b.properties.index) {
        return -1;
      } else {
        return 0;
      }
    });
    features.forEach(feature => {
      const name = feature.properties.name;
      const textSize = WRAP.Geo.getTextMetrics(name, fontSize);
      const textWidth = textSize[0];
      const textHeight = textSize[1];
      const point = feature.geometry.coordinates.slice(0, feature.geometry.coordinates.length - 1);
      const eruptedVolcano = this.latestVolcanoList.find(e=> { return e.id===name && e.eruptedFlag === 1});
      if(!mask.intersectRect(point, offsetX, offsetY, textWidth, textHeight) || eruptedVolcano) {
        const volcanoLabel = new WRAP.Geo.Feature.Text({
          align: "left",
          fillStyle: fillStyle,
          fontSize: fontSize,
          offsetX: offsetX,
          offsetY: offsetY,
          point: point,
          text: name,
        });
        this.labelLayer.addFeature(volcanoLabel);
        mask.addRect(point, offsetX, offsetY, textWidth, textHeight);

        // 噴火フラグが立っている火山に噴火時刻のラベルを設定
        if(eruptedVolcano) {
          const eruptionTime = new WRAP.Geo.Feature.Text({
            align: "left",
            fillStyle: fillStyle,
            fontSize: fontSize,
            offsetX: -75,
            offsetY: 18,
            point: point,
            text: formatUTCDate(parseUTCDate(eruptedVolcano.latestErupted), '%Y/%m/%d %H:%MZ').split(" ")
          })
          this.labelLayer.addFeature(eruptionTime);
        }
      }
    });
  }

  _altitudeTooltip(altitude) {
    return this._convertAltitudeToNearFL(altitude);
  }

  acosFL = [
    430,420,410,400,
    390,380,370,360,350,340,330,320,310,300,
    290,280,270,260,250,240,230,220,210,200,
    190,180,170,160,150,140,130,120,110,100,
    90,80,70,60,50,40,30,20,10,0
  ];
  _convertAltitudeToNearFL(altitude) {
    const near = this.acosFL.reduce((prev, curr) => {
      return Math.abs(curr - altitude) < Math.abs(prev - altitude) ? curr : prev
    });
    let fl;
    if(near === 0) {
      fl = "ALL";
    } else {
      fl = `FL${('000' + near).slice(-3)}`;
    }
    return fl;
  }

  _acosTouchHandler(layer, feature) {
    if(layer) {
      // 火山クリック
      if(layer.name() === Acos.RankLayerName) {
        let target = this.popupVolcanoList.find(v=>v.id === feature.geo.properties.name);
        if(!target) {
          return;
        }

        // Levelの選択値をmeter単位に変換
        const selectLevelMeter = Math.floor(ft2m(parseInt(this.level.replace('FL', ''), 10) * 100));
        let targetDetail;
        // levelがALLでない場合は2D、それ以外は3Dを使用
        if(selectLevelMeter) {
          targetDetail = this.volcanoAsh3D[target.id];
        } else {
          targetDetail = this.volcanoAsh2D[target.id];
        }

        const latestData = Object.values(targetDetail.data).sort((a,b)=>{
          if(a.eruptedDate < b.eruptedDate) {
              return 1;
          }else if(a.eruptedDate > b.eruptedDate) {
              return -1;
          } else {
              return 0;
          }
        })[0];

        const announcedTime = (new WRAP.Core.DateTime(latestData.eruptedDate)).text('YYYY/MM/DD hh:mmZ');
        const flList = [];
        latestData.ft.forEach((ft, index) => {
          const fl = [];
          const tempFt = ('0' + ft.ft).slice(-2);
          // FT15と21は表示しない
          if(tempFt !== '15' && tempFt !== '21') {
            fl.push(<p key={`fl${index}`}>FT{tempFt}: {(new WRAP.Core.DateTime(ft.fth)).text('YYYY/MM/DD hh:mmZ')}</p>);
            fl.push(<p key={`level${index}`}>Level: {this.level}</p>);

            // 選択中のLEVELに該当するデータに絞り込む
            var coordinates = '';
            if(selectLevelMeter) {
              coordinates = ft.coordinates.find(c=> {
                return c[0][0][2] >= selectLevelMeter;
              })
            } else {
              coordinates = ft.coordinates;
            }
    
            let coordinateText = "";
            if(coordinates) {
              coordinates[0].forEach(coordinate => {
                coordinateText += this._changeLatLonForView(coordinate[1], coordinate[0]);
                coordinateText += ' - ';
              });
              coordinateText = coordinateText.slice(0, -3);
            } else {
              coordinateText = 'NIL';
            }
            fl.push(<p key={`coordinate${index}`}>{coordinateText}</p>);
            flList.push(<div key={index} className="acos-popup-fl">{fl}</div>);
          }
        });

        if(!this.popup) {
          this.popup = document.createElement('div');
          document.getElementById('map').appendChild(this.popup);
        }
        reactDom.render(
          <Draggable>
            <div id="acos-popup" className="acos-popup">
              <div className="acos-popup-header">
                <div className="acos-popup-title">ACOS</div>
                <button className="acos-popup-close" onClick={this._closeClickHandler.bind(this)}>×</button>
              </div>
              <div className="acos-popup-announced-time">Announced Time {announcedTime}</div>
              <div className="acos-popup-detail">
                <p>Announced = {announcedTime}</p>
                <p>Volcano = {target.id}</p>
                <p>Loc = {this._changeLatLonForView(latestData.lat, latestData.lon)}</p>
                <p>Height = {Math.floor(m2ft(latestData.height))}ft</p>
                <p>Status Code = {latestData.statusCode}</p>
                <p>Erupted = {announcedTime}</p>
                <p>MAX VA Height = {Math.floor(m2ft(latestData.maxVaHeight))}ft</p>
                {flList}
              </div>
            </div>
          </Draggable>
        , this.popup);
        this.popup.style.display = "block";
      }
    }
  }

  _closeClickHandler() {
    this.popup.style.display = "none";
  }

  _changeLatLonForView(lat, lon) {
    let sn = 'N';
    let ew = 'E';
    if(lat === 0) {
      sn = '';
    } else if(lat < 0) {
      sn = 'S';
    }
    lon = this._convertTo180lon(lon);
    if(lon === 0 || lon === 180 || lon === -180) {
      ew = '';
    } else if(lon < 0) {
      ew = 'W';
    }
    return sn + Math.abs(lat) + " " + ew + Math.abs(lon);
  }

  /**
   * 経度を180度表記に変換する
   * @param {number} lon 経度
   * @returns {number}
   */
  _convertTo180lon(lon) {

    // 180度以内だったら変換不要
    if(lon <= 180) {
      return lon;
    }
    // 小数点以下の桁数を取得
    const numList = (lon + '').split('.');
    let decimalLength = 0;
    if(numList[1] !== undefined) {
      decimalLength = numList[1].length;
    }
    // 整数値に変化、桁数を合わせて0度からの度数を算出
    const i = Math.pow(10, decimalLength);
    const converted180 = 180 * i;
    const calculationResult = -(converted180 - (lon * i - converted180));

    return calculationResult / i;
  }

  async _loadVolcanicAshConcVector() {

    return new Promise(resolve => {
      
      const promises = [];
      const wrapUrl = WrapOption().wrap_url;
      const ash2d = [];
      const ash3d = [];
      axios.get(`${wrapUrl}/WX_WNI_VolcanicAshConcVector/index.json?t=${new Date().getTime()}`).then((res) => {
        this.rankIndex = res.data.index;
        const volcanos = Object.keys(this.rankIndex);
        const d = Date.now();
        for(const volcano of volcanos) {
          promises.push(axios.get(`${wrapUrl}/WX_WNI_VolcanicAshConcVector/2D/${volcano}.json?t=${d}`).then(res => {
            ash2d.push(res.data.features);
          }));
          promises.push(axios.get(`${wrapUrl}/WX_WNI_VolcanicAshConcVector/3D/${volcano}.json?t=${d}`).then(res => {
            ash3d.push(res.data.features);
          }));
        }

        Promise.all(promises).then(refs => {
          this.volcanoAsh2D = this._shapeVolcanicAshConcVectorDetail(ash2d);
          this.volcanoAsh3D = this._shapeVolcanicAshConcVectorDetail(ash3d);
          resolve(refs);
        });
      });
    });
  }

  _shapeVolcanicAshConcVectorDetail(volcanosFeatures) {

    const ashConcVectorDetail = {};

    volcanosFeatures.forEach(volcanoFeatures => {
      let volcanoName = '';
      const volcanoData = {
        data: {}
      };
      volcanoFeatures.forEach(feature => {
        // properties.wrap_valid_dateが無ければヘッダー用のデータ
        if(!feature.properties.wrap_valid_date) {

          volcanoName = feature.name;

          if(!volcanoData.data[feature.erupted_date]) {
            volcanoData.data[feature.erupted_date] = {
              lat: feature.geometry.coordinates[1],
              lon: feature.geometry.coordinates[0],
              height: feature.geometry.coordinates[2],
              statusCode: feature.properties.index,
              eruptedDate: feature.erupted_date,
              issueDate: feature.issued_date,
              maxVaHeight: feature.properties.max_height,
              ft: []
            };
          }

        } else {
          const eruptedDate = new WRAP.Core.DateTime(feature.erupted_date);
          const valid = new WRAP.Core.DateTime(feature.properties.wrap_valid_date);
          const dateDiff = eruptedDate.diff(valid);
          const ftt = parseInt(dateDiff / (60 * 60), 10);

          volcanoData.data[feature.erupted_date].ft.push({
            fth: feature.properties.wrap_valid_date,
            coordinates: feature.geometry.coordinates,
            ft: ftt
          });
        }
      });
      ashConcVectorDetail[volcanoName] = volcanoData;
    });
    return ashConcVectorDetail;
  }

  /**
   * 
   * @param {Number} meter 
   * @returns 
   */
  _meter2ft(meter) {
    const feet = m2ft(meter);
    const flooredFeet = Math.round(feet / 100);
    return ('0' + flooredFeet).slice(-3);
  }

  /**
   * popupWindowに詳細を表示する火山のリストを作る
   */
  _createPopupVolcanoList() {
    const nonFloatingAshVolcanoList = this.latestVolcanoList.filter(v=>v.floatingAshFlag === 0);
    this.popupVolcanoList = nonFloatingAshVolcanoList.filter(x => this.rankIndex[x.id]);
  }

  _updateBounds() {
    this._addLabel();
  }

  applyMapColor(mapColor) {
    this.mapColor = mapColor;
    if(this.ready) {
      this._addLabel();
    }
	}
}

export default Acos;
